import { useState, useEffect, useMemo } from "react";
import "@/App.css";
import { Toaster } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Building2, TrendingUp, LayoutDashboard, Sparkles, UserCheck, Loader2, Package } from "lucide-react";
import Header from "@/components/Header";
import CondominiosTab from "@/components/CondominiosTab";
import FunilTab from "@/components/FunilTab";
import ExecutivoTab from "@/components/ExecutivoTab";
import ClientesTab from "@/components/ClientesTab";
import PlanosTab from "@/components/PlanosTab";
import LoginPage from "@/components/LoginPage";
import OnboardingForm from "@/components/OnboardingForm";
import { AuthProvider, useAuth, isAdmin, hasPermission } from "@/lib/auth";

const LOGO_URL =
  "https://customer-assets-agu9un31.emergentagent.net/job_sales-tracker-1336/artifacts/xrne8mgy_versao_branca_03.webp";

// Cada aba requer 1 permissão de view para aparecer no menu principal.
const ALL_TABS = [
  { id: "visao", label: "Visão Geral", icon: LayoutDashboard, perm: "visao_geral.view" },
  { id: "condominios", label: "Condomínios", icon: Building2, perm: "condominios.view" },
  { id: "funil", label: "Funil Semanal", icon: TrendingUp, perm: "funil.view" },
  { id: "clientes", label: "Clientes", icon: UserCheck, perm: "clientes.view" },
  { id: "planos", label: "Planos", icon: Package, perm: "planos.view" },
];

const ROLE_LABEL = { admin: "Painel do gestor", tecnico: "Painel do técnico", vendedora: null };

function AppShell() {
  const { user, loading } = useAuth();
  const admin = isAdmin(user);

  const TABS = useMemo(
    () => ALL_TABS.filter((t) => hasPermission(user, t.perm)),
    [user]
  );

  const [activeTab, setActiveTab] = useState(TABS[0]?.id || "visao");
  const [theme, setTheme] = useState(() => localStorage.getItem("cs360-theme") || "dark");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("cs360-theme", theme);
  }, [theme]);

  // Reset tab quando papel/permissões mudam ou a aba atual sumir
  useEffect(() => {
    if (TABS.length > 0 && !TABS.find((t) => t.id === activeTab)) {
      setActiveTab(TABS[0].id);
    }
  }, [TABS, activeTab]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground grid place-items-center">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Carregando…
        </div>
      </div>
    );
  }

  if (!user) return <LoginPage />;

  const roleGreet = ROLE_LABEL[user.role] || `Olá, ${user.name}`;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="app-root">
      <Toaster
        position="top-right"
        theme={theme}
        closeButton
        toastOptions={{ classNames: { toast: "flua-toast" }, duration: 4500 }}
      />
      <Header theme={theme} setTheme={setTheme} />

      <main className="max-w-[1400px] mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Hero — assinatura FLUA: gradiente azul + linhas fluidas SVG */}
        <section className="rounded-3xl flua-hero text-primary-foreground p-6 sm:p-10 lg:p-12">
          <div className="relative flex items-start justify-between gap-6 flex-wrap">
            <div className="min-w-0 max-w-3xl">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-white/70 mb-4">
                <Sparkles className="w-3.5 h-3.5" />
                <span>FLUA · Highconnection Internet</span>
                <span className="hidden sm:inline text-white/40">·</span>
                <span className="hidden sm:inline">{roleGreet}</span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02]">
                {admin ? <>Controle comercial de <span className="font-light italic text-white/90">ponta a ponta</span></>
                  : user.role === "tecnico"
                    ? <>Operação de <span className="font-light italic text-white/90">infraestrutura</span></>
                    : <>Seus clientes e sua <span className="font-light italic text-white/90">comissão</span></>}
              </h1>
              <p className="mt-4 text-sm sm:text-base text-white/75 max-w-2xl font-light leading-relaxed">
                {admin
                  ? "Metas por condomínio, funil semanal, relacionamento com síndicos e comissões — em um só painel."
                  : user.role === "tecnico"
                    ? "Aprove infraestruturas, acompanhe planos e produtividade da equipe."
                    : "Acompanhe seus contratos fechados, sua meta e sua comissão do mês."}
              </p>
              <div className="mt-6 flex items-center gap-2 text-[11px] text-white/60 font-light italic">
                <span className="inline-block w-8 h-px bg-white/40" />
                Navegue na onda da inovação com a conexão que eleva o seu mundo.
              </div>
            </div>
            <div className="relative shrink-0">
              <div className="absolute -inset-4 rounded-full bg-white/10 blur-xl" />
              <img src={LOGO_URL} alt="FLUA" className="relative h-16 sm:h-20 w-auto flua-flow-anim" data-testid="hero-logo" />
            </div>
          </div>
        </section>

        {/* Tabs — pill com sombra FLUA */}
        {TABS.length > 1 && (
          <div className="flex items-center gap-1 p-1.5 rounded-2xl border border-border bg-card/80 backdrop-blur overflow-x-auto card-flua">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`nav-tab-${t.id}`}
                  onClick={() => setActiveTab(t.id)}
                  className={`relative flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors whitespace-nowrap ${active ? "text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                >
                  {active && (
                    <motion.div layoutId="tab-pill" className="absolute inset-0 bg-primary rounded-xl shadow-lg shadow-primary/30"
                      transition={{ type: "spring", stiffness: 400, damping: 32 }} />
                  )}
                  <span className="relative flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    {t.label}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Content — cada aba verifica permissão específica */}
        <AnimatePresence mode="wait">
          <motion.div key={activeTab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
            {activeTab === "visao" && hasPermission(user, "visao_geral.view") && (
              <ExecutivoTab onNavigateCondominios={() => setActiveTab("condominios")} />
            )}
            {activeTab === "condominios" && hasPermission(user, "condominios.view") && <CondominiosTab />}
            {activeTab === "funil" && hasPermission(user, "funil.view") && <FunilTab />}
            {activeTab === "clientes" && hasPermission(user, "clientes.view") && <ClientesTab />}
            {activeTab === "planos" && hasPermission(user, "planos.view") && <PlanosTab />}
          </motion.div>
        </AnimatePresence>

        <footer className="pt-8 pb-4 text-center space-y-1">
          <div className="text-xs text-muted-foreground">FLUA · Painel comercial de ponta a ponta</div>
          <div className="text-[10px] text-muted-foreground/70 italic font-light">
            Navegue na onda da inovação com a conexão que eleva o seu mundo.
          </div>
        </footer>
      </main>
    </div>
  );
}

export default function App() {
  // Rota pública `/onboard` — formulário para o cliente final preencher.
  // Renderizada antes do AuthProvider pra não precisar de login/cookie.
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/onboard")) {
    return (
      <>
        <Toaster
          position="top-right"
          theme="dark"
          closeButton
          toastOptions={{ classNames: { toast: "flua-toast" }, duration: 4500 }}
        />
        <OnboardingForm />
      </>
    );
  }
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}
